import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';

interface AlarmPopupProps {
  isVisible: boolean;
  alarmTime: string;
  alarmType: '1M' | '3M' | '5M';
  alarmName?: string;
  /** 이번에 울린 구간(분). 예: 3분전 알람이 2분전에 처음 켜지면 2 → 타이머 전체 2분 */
  phaseMinutes?: number;
  onStopAlarm: () => void;
  onClosePopup: () => void;
}

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const PopupContent = styled.div`
  background: var(--color-surface);
  border-radius: clamp(8px, 2vw, 16px);
  padding: clamp(15px, 3vh, 40px) clamp(15px, 4vw, 60px);
  text-align: center;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-shell-border);
  animation: slideIn 0.3s ease-out;
  width: 80%;
  height: 80%;
  max-width: 1400px;
  max-height: 900px;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  overflow: auto;

  @keyframes slideIn {
    from {
      transform: translateY(-50px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

// 알람시각 + 알람
const AlarmTitleRow = styled.div<{ $modalWidth?: number }>`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: clamp(12px, 2vw, 20px);
  margin-bottom: clamp(5px, 1vh, 10px);
  flex-shrink: 0;
  width: 100%;
`;

const AlarmTimeDisplay = styled.span<{ $modalWidth?: number }>`
  font-size: ${props => {
    if (!props.$modalWidth) return '3.5rem';
    // 모달 너비가 400px 이상이면 3.5rem 유지, 그보다 작으면 비례적으로 줄어듦
    const baseSize = 3.5;
    const minSize = 1.4;
    const breakpoint = 400;
    if (props.$modalWidth >= breakpoint) return `${baseSize}rem`;
    const ratio = props.$modalWidth / breakpoint;
    const size = baseSize * ratio;
    return `${Math.max(minSize, size)}rem`;
  }};
  font-weight: bold;
  color: var(--color-accent-text);
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  transition: font-size 0.2s ease;
`;

const AlarmLabel = styled.span<{ $modalWidth?: number }>`
  font-size: ${props => {
    if (!props.$modalWidth) return '3rem';
    const baseSize = 3;
    const minSize = 1.2;
    const breakpoint = 400;
    if (props.$modalWidth >= breakpoint) return `${baseSize}rem`;
    const ratio = props.$modalWidth / breakpoint;
    const size = baseSize * ratio;
    return `${Math.max(minSize, size)}rem`;
  }};
  font-weight: bold;
  color: var(--color-text-primary);
  line-height: 1;
  transition: font-size 0.2s ease;
`;

// 알람 종류 (알람시각 바로 밑)
const AlarmTypeRow = styled.div<{ $modalWidth?: number }>`
  font-size: ${props => {
    if (!props.$modalWidth) return '1.2rem';
    const baseSize = 1.2;
    const minSize = 0.75;
    const breakpoint = 400;
    if (props.$modalWidth >= breakpoint) return `${baseSize}rem`;
    const ratio = props.$modalWidth / breakpoint;
    const size = baseSize * ratio;
    return `${Math.max(minSize, size)}rem`;
  }};
  color: var(--color-text-gray);
  margin-top: clamp(2px, 0.5vh, 5px);
  margin-bottom: clamp(8px, 2vh, 20px);
  text-align: center;
  flex-shrink: 0;
  transition: font-size 0.2s ease;
`;

// 원형 타이머 컨테이너
const TimerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: clamp(20px, 4vh, 40px) 0;
  flex: 1;
  min-height: 0;
  flex-shrink: 1;
  width: 100%;
`;

const TimerCircle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  overflow: visible;
`;

const TimerText = styled.div<{ $timerTextSize?: number }>`
  position: absolute;
  font-size: ${props => props.$timerTextSize ? `${props.$timerTextSize}rem` : '5.5rem'};
  font-weight: bold;
  color: var(--color-accent-text);
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  transition: font-size 0.2s ease;
  white-space: nowrap;
  overflow: visible;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: clamp(16px, 3vw, 24px);
  justify-content: center;
  margin-top: clamp(20px, 4vh, 40px);
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const Button = styled.button<{ primary?: boolean }>`
  padding: clamp(6px, 1.5vh, 12px) clamp(16px, 3vw, 36px);
  border: none;
  border-radius: clamp(4px, 1vw, 8px);
  font-size: clamp(0.85rem, 1.8vw, 1.3rem);
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: clamp(80px, 15vw, 180px);

  ${props => props.primary ? `
    background: var(--color-accent-strong);
    color: white;
    
    &:hover {
      background: var(--color-accent);
    }
  ` : `
    background: var(--color-border);
    color: var(--color-text-primary);
    
    &:hover {
      background: var(--color-border);
    }
  `}

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;

const getAlarmTypeText = (type: '1M' | '3M' | '5M'): string => {
  switch (type) {
    case '1M': return '-1분전 알람';
    case '3M': return '-3분전 알람';
    case '5M': return '-5분전 알람';
    default: return '알람';
  }
};

const getAlarmMinutes = (type: '1M' | '3M' | '5M'): number => {
  switch (type) {
    case '1M': return 1;
    case '3M': return 3;
    case '5M': return 5;
    default: return 1;
  }
};

const AlarmPopup: React.FC<AlarmPopupProps> = ({
  isVisible,
  alarmTime,
  alarmType,
  alarmName,
  phaseMinutes,
  onStopAlarm,
  onClosePopup
}) => {
  const typeMaxMinutes = getAlarmMinutes(alarmType);
  const totalMinutes = useMemo(() => {
    if (phaseMinutes != null && phaseMinutes >= 1) {
      return Math.min(phaseMinutes, typeMaxMinutes);
    }
    return typeMaxMinutes;
  }, [phaseMinutes, typeMaxMinutes, alarmType]);

  const totalSeconds = totalMinutes * 60;
  const [remainingSeconds, setRemainingSeconds] = useState(totalSeconds);
  const timerContainerRef = useRef<HTMLDivElement>(null);
  const popupContentRef = useRef<HTMLDivElement>(null);
  const timerTextRef = useRef<HTMLDivElement>(null);
  const [circleSize, setCircleSize] = useState(350);
  const [modalWidth, setModalWidth] = useState(0);
  const [timerTextSize, setTimerTextSize] = useState(5.5);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null); // 타이머 인터벌 참조
  const lastAlarmKey = useRef<string>(''); // 이전 알람 정보 추적

  // 다크 모드 확인 (실시간으로 확인하기 위해 state 사용)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return document.documentElement.getAttribute('data-theme') !== 'light';
  });

  // 테마 변경 감지
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.getAttribute('data-theme') !== 'light');
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // 알람 정보(타입, 시간, 이번 구간 분)가 변경될 때만 타이머 초기화
  useEffect(() => {
    const currentAlarmKey = `${alarmType}-${alarmTime}-${phaseMinutes ?? 'full'}`;
    const newTotalSeconds = totalMinutes * 60;
    
    // 알람 정보가 변경되었을 때만 타이머 초기화
    if (lastAlarmKey.current !== currentAlarmKey) {
      setRemainingSeconds(newTotalSeconds);
      lastAlarmKey.current = currentAlarmKey;
      
      // 기존 타이머 정리 후 재시작
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      
      // 새 타이머 시작
      if (newTotalSeconds > 0) {
        timerIntervalRef.current = setInterval(() => {
          setRemainingSeconds((prev) => {
            if (prev <= 1) {
              if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
              }
              // 0에 도달해도 자동 종료하지 않음
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    }
  }, [alarmType, alarmTime, totalMinutes, phaseMinutes]);

  // 타이머 카운트다운 (처음 마운트 시에만 시작, 알람 정보 변경 시에는 위의 useEffect에서 처리)
  useEffect(() => {
    // 이미 interval이 실행 중이면 추가로 생성하지 않음
    if (timerIntervalRef.current) {
      return;
    }

    // 처음 마운트될 때만 타이머 시작
    timerIntervalRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          // 0에 도달해도 자동 종료하지 않음
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []); // 마운트 시 한 번만 실행

  // 타이머가 0이 된 상태에서 모달을 닫으면 타이머 초기화
  useEffect(() => {
    if (!isVisible && remainingSeconds === 0) {
      const newTotalSeconds = totalMinutes * 60;
      setRemainingSeconds(newTotalSeconds);
      lastAlarmKey.current = ''; // 다음에 다시 열 때 초기화되도록
    }
  }, [isVisible, remainingSeconds, totalMinutes]);

  // 시간 포맷팅 함수 (useEffect 외부로 이동)
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // 모달 크기 측정 및 타이머 원 크기 조정
  useEffect(() => {
    if (!popupContentRef.current || !timerContainerRef.current) return;

    const updateSizes = () => {
      const popup = popupContentRef.current;
      const container = timerContainerRef.current;
      if (!popup || !container) return;
      
      // 모달의 실제 너비 측정
      const popupRect = popup.getBoundingClientRect();
      const currentModalWidth = popupRect.width;
      // 값이 실제로 변경될 때만 상태 업데이트 (무한 루프 방지)
      setModalWidth(prev => {
        if (Math.abs(prev - currentModalWidth) < 1) return prev; // 1px 차이 무시
        return currentModalWidth;
      });
      
      // 타이머 컨테이너의 실제 크기 측정
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      
      // 컨테이너의 computed style을 가져와 패딩 확인
      const containerStyle = window.getComputedStyle(container);
      const paddingLeft = parseFloat(containerStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(containerStyle.paddingRight) || 0;
      const paddingTop = parseFloat(containerStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(containerStyle.paddingBottom) || 0;
      
      // 사용 가능한 실제 크기 계산 (패딩 제외)
      const availableWidth = containerWidth - paddingLeft - paddingRight;
      const availableHeight = containerHeight - paddingTop - paddingBottom;
      
      // 가로와 세로 중 작은 값 사용, 여유 공간을 위해 98% 사용 (더 크게)
      const availableSize = Math.min(availableWidth, availableHeight) * 0.98;
      // 최소 120px, 최대 600px로 제한 (더 크게)
      const size = Math.max(120, Math.min(availableSize, 600));
      // 값이 실제로 변경될 때만 상태 업데이트 (무한 루프 방지)
      setCircleSize(prev => {
        if (Math.abs(prev - size) < 1) return prev; // 1px 차이 무시
        return size;
      });
      
      // 타이머 텍스트 크기 조정
      // 원 크기에 비례한 기본 크기 (더 크게)
      const circleBasedSize = size * 0.1;
      
      // 모달 너비를 고려한 최대 크기
      if (currentModalWidth > 0) {
        // "00:00" 텍스트가 모달 너비의 65%를 넘지 않도록
        const maxWidthForText = currentModalWidth * 0.65;
        
        // 실제 텍스트 너비를 측정하기 위한 임시 요소 생성
        const tempSpan = document.createElement('span');
        tempSpan.style.fontFamily = 'Rajdhani, Orbitron, DS-Digital, LCD, Courier New, monospace';
        tempSpan.style.fontWeight = 'bold';
        tempSpan.style.fontSize = '5.5rem';
        tempSpan.style.visibility = 'hidden';
        tempSpan.style.position = 'absolute';
        tempSpan.style.whiteSpace = 'nowrap';
        tempSpan.style.top = '-9999px';
        tempSpan.textContent = formatTime(remainingSeconds);
        document.body.appendChild(tempSpan);
        
        const textWidthAtMax = tempSpan.offsetWidth;
        document.body.removeChild(tempSpan);
        
        // 텍스트가 모달 너비를 넘으면 크기 조정
        let calculatedSize = 5.5;
        if (textWidthAtMax > maxWidthForText) {
          calculatedSize = (maxWidthForText / textWidthAtMax) * 5.5 * 0.9; // 여유 공간 90%
        }
        
        // 원 크기 기준과 모달 너비 기준 중 작은 값 사용
        const finalSize = Math.min(circleBasedSize, calculatedSize);
        const newTimerTextSize = Math.max(1.4, Math.min(finalSize, 5.5));
        // 값이 실제로 변경될 때만 상태 업데이트 (무한 루프 방지)
        setTimerTextSize(prev => {
          if (Math.abs(prev - newTimerTextSize) < 0.01) return prev; // 0.01 차이 무시
          return newTimerTextSize;
        });
      } else {
        // 모달 너비를 아직 측정하지 못한 경우 원 크기 기준만 사용
        const newTimerTextSize = Math.max(1.4, Math.min(circleBasedSize, 5.5));
        // 값이 실제로 변경될 때만 상태 업데이트 (무한 루프 방지)
        setTimerTextSize(prev => {
          if (Math.abs(prev - newTimerTextSize) < 0.01) return prev; // 0.01 차이 무시
          return newTimerTextSize;
        });
      }
    };

    // 초기 크기 설정
    const initTimer = setTimeout(() => {
      updateSizes();
    }, 0);
    
    // ResizeObserver로 모달 및 타이머 컨테이너 크기 변화 감지
    const resizeObserver = new ResizeObserver(() => {
      // requestAnimationFrame을 사용하여 리플로우 후 측정
      requestAnimationFrame(() => {
        updateSizes();
      });
    });
    
    // remainingSeconds가 변경될 때도 텍스트 크기 재계산
    
    if (popupContentRef.current) {
      resizeObserver.observe(popupContentRef.current);
    }
    if (timerContainerRef.current) {
      resizeObserver.observe(timerContainerRef.current);
    }

    // window resize 이벤트도 추가로 감지
    window.addEventListener('resize', updateSizes);

    return () => {
      clearTimeout(initTimer);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSizes);
    };
  }, [formatTime, isVisible, remainingSeconds]);

  // 원형 진행률 계산 (0 ~ 1)
  const progress = remainingSeconds / totalSeconds;

  // SVG 원의 둘레 계산 (반지름을 동적으로 계산)
  const strokeWidth = useMemo(() => {
    // 선 두께는 적당한 크기로 유지 (막대가 너무 두껍지 않도록)
    return Math.max(16, circleSize * 0.06);
  }, [circleSize]);
  
  const radius = useMemo(() => {
    // 원 크기의 48% 정도가 반지름이 되도록 (strokeWidth의 절반만큼 여유 공간 확보)
    // SVG 경계에서 strokeWidth/2만큼의 여유 공간을 확보하면 잘리지 않음
    const maxRadius = (circleSize / 2) - (strokeWidth / 2) - 4; // 4px 추가 여유 공간
    const preferredRadius = circleSize * 0.48;
    return Math.min(maxRadius, preferredRadius);
  }, [circleSize, strokeWidth]);
  
  const circumference = useMemo(() => {
    return 2 * Math.PI * radius;
  }, [radius]);
  
  const strokeDashoffset = circumference * (1 - progress);
  const center = circleSize / 2;

  if (!isVisible) return null;

  return createPortal(
    <PopupOverlay>
      <PopupContent ref={popupContentRef}>
        {/* 알람시각 + 알람 */}
        <AlarmTitleRow $modalWidth={modalWidth}>
          <AlarmTimeDisplay $modalWidth={modalWidth}>{alarmTime || '00:00'}</AlarmTimeDisplay>
          <AlarmLabel $modalWidth={modalWidth}>알람</AlarmLabel>
        </AlarmTitleRow>

        {/* 알람 종류 (알람시각 바로 밑) */}
        <AlarmTypeRow $modalWidth={modalWidth}>{getAlarmTypeText(alarmType)}</AlarmTypeRow>

        {/* 원형 타이머 */}
        <TimerContainer ref={timerContainerRef}>
          <TimerCircle>
            <svg 
              width={circleSize} 
              height={circleSize} 
              viewBox={`0 0 ${circleSize} ${circleSize}`}
              style={{ overflow: 'visible' }}
            >
              {/* 배경 원 */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="var(--color-border)"
                strokeWidth={strokeWidth}
              />
              {/* 진행 원 */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={isDarkMode ? '#ffffff' : 'var(--color-accent-strong)'}
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                transform={`rotate(-90 ${center} ${center})`}
                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
              />
            </svg>
            <TimerText ref={timerTextRef} $timerTextSize={timerTextSize}>{formatTime(remainingSeconds)}</TimerText>
          </TimerCircle>
        </TimerContainer>

        <ButtonGroup>
          <Button primary onClick={onStopAlarm}>
            알람끄기
          </Button>
          <Button onClick={onClosePopup}>
            팝업닫기
          </Button>
        </ButtonGroup>
      </PopupContent>
    </PopupOverlay>,
    document.body
  );
};

export default AlarmPopup; 